/*
 * Copyright (c) 2026. Tooling Team
 */

// mapElements.columns[].key uses a different naming convention than the
// record's own targetConcept field (e.g. key "standardCodeDescription" vs.
// record.standardDescription, both from the backend) - maps the former to
// the latter so the column can accessor into the flattened record.
// TODO: backend defect - column key and record field naming should match;
// remove this alias once the backend aligns mapElements.columns[].key with
// the targetConcept field names.
const ACCESSOR_KEY_ALIASES = {
  standardCodeDescription: "standardDescription",
  standardCodeLongDescription: "standardLongDescription",
};

// Long description text is folded into the Description column's expandable
// cell (and the CSV additional-details columns) rather than rendered as its
// own grid column.
const HIDDEN_KEYS = new Set(["standardCodeLongDescription"]);

const MIN_SIZE = 150;

// Builds EIPGrid's API-driven data columns (i.e. everything except the
// frontend-only Select/State columns, which have no backing entry in
// mapElements.columns) directly off the API response's column metadata -
// order (including where Tags lands), section grouping and label all come
// from `apiColumns` (deriveMapColumnSections in utils/funcs.js decides
// whether Tags is present at all and where it sits).
// `renderers` supplies the header/cell rendering closures owned by EIPGrid
// (renderSectionHeader, renderUnsectionedHeader, getCellRenderer,
// getCsvFormatter) since those depend on component state (e.g.
// showFullLocalDescription) or helpers (moment formatting) that live there.
export function buildEipGridColumns(apiColumns, renderers) {
  const columnsMeta = Array.isArray(apiColumns) ? apiColumns : [];

  return columnsMeta
    .filter((column) => column?.key && !HIDDEN_KEYS.has(column.key))
    .map(({ key, primaryName, section }) => {
      const accessorKey = ACCESSOR_KEY_ALIASES[key] || key;
      const label = primaryName || key;
      const headerLabel = section ? `${section} ${label}` : label;
      const csvFormatter = renderers.getCsvFormatter?.(key);
      const accessorFn = renderers.getAccessorFn?.(key, accessorKey);
      const sortingFn = renderers.getSortingFn?.(key);

      return {
        header: section
          ? renderers.renderSectionHeader(section, label)
          : renderers.renderUnsectionedHeader(label),
        accessorKey,
        ...(accessorFn ? { accessorFn } : {}),
        cell: renderers.getCellRenderer(key, accessorKey),
        meta: {
          headerLabel,
          section,
          ...(csvFormatter ? { customSetCellCsv: csvFormatter } : {}),
        },
        minSize: MIN_SIZE,
        ...(sortingFn ? { sortingFn } : {}),
      };
    });
}
